/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  Typography,
} from '@mui/material';
import { adaptV4Theme, createTheme, Theme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/styles';
import { StyledEngineProvider } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Scrollbars from 'react-custom-scrollbars-2';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { VariableSizeList } from 'react-window';
import Terminal from '../../shared/entities/terminal';
import { selectIsInitialTerminalsRequestFinished, TerminalListItem } from '../../store';
import { SortDirection } from '../../store/ui/reducer';
import { SCROLLBARS_THUMB_VERTICAL_STYLE } from '../common/layout/Scrollbars';
import TerminalListConnectivityHeaderIcon from '../common/TerminalListConnectivityHeaderIcon';
import { KU_PLUS_KA_DRAWER_WIDTH_PX } from './FleetMap';
import PlatformListItemComponent from './PlatformListListItem';
import appTheme from '../common/theme/appTheme.json';
import { concat } from 'lodash';
import {
  PlatformListGroup,
  SpaceVehicleGroups,
  NetworkGroup,
  TerminalGroup,
  GroupListItem,
} from '../../shared/entities/platformListGroup';
import { TerminalPlatformsEnum } from '../../shared/entities/terminalPlatform';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';

const ROW_HEIGHT_NO_PADDING = 51;
const ROW_HEIGHT_BASE_PX = 56;
const ROW_HEIGHT_PADDED_PX = 61;
const MULTI_ROW_HEIGHT_BASE_PX = 128;
const GROUP_HEADER_HEIGHT_PX = 45.5;

const baseTheme = createTheme(
  adaptV4Theme({
    typography: {
      fontFamily: `'Source Sans Pro', sans-serif`,
    },
    palette: {
      primary: { main: '#5EA215' },
      secondary: { main: '#61DED8' },
      background: {
        paper: '#202E39',
        default: '#142431',
      },
      text: { primary: '#ffffff' },
      action: { hover: '#0D1418', selected: '#000000' },
      divider: '#465967',
    },
  }),
);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    visibilityToggleContainer: {
      right: 0,
      paddingRight: '8px',
    },
    visibilityIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
    collapseIcon: {
      maxHeight: '18px',
      maxWidth: '18px',
      color: '#C3CDD5',
    },
  }),
);

// This is the new updated terminal list drawer from the KuPluKa enhancement with the expandable and collapsible list items
export default function PlatformList(props: {
  listHeightPx: number;
  terminalListItems:
    | TerminalGroup<TerminalListItem[]>
    | SpaceVehicleGroups<TerminalListItem[]>
    | NetworkGroup<TerminalListItem[]>;
  hiddenDatapathUuids: string[];
  collapsedDatapathUuids: { [datapathUuid: string]: boolean };
  selectedDatapathUuid?: string;
  selectedTerminalTabIndex?: number;
  terminalListSortDirection: { [key in PlatformListGroup]: SortDirection } | string; // Sort Direction for TerminalsInYourBeamPanel is still just a string
  terminalListSortField: string;
  terminals: TerminalGroup<Terminal[]> | SpaceVehicleGroups<Terminal[]> | NetworkGroup<Terminal[]>;
  terminalMap: { [datapathUuid: string]: Terminal };
  hideTtho?: boolean;
  listDrawerOpen: boolean;

  // Header Actions
  onSortColumnHeaderClickedHandler: (sortField: string, sortDirection: SortDirection, groupName: string) => void;
  onHideGroupClickedHandler: (event: React.MouseEvent, groupName: string) => void;
  onShowGroupClickedHandler: (event: React.MouseEvent, groupName: string) => void;
  onCollapseGroupClickedHandler: (event: React.MouseEvent, groupName: string) => void;
  onExpandGroupClickedHandler: (event: React.MouseEvent, groupName: string) => void;
  openListDrawer: () => {
    type: string;
    payload: any;
    meta: undefined;
  };

  // Item Actions
  onRowClickedHandler: (terminal: string, index: number) => void;
  onRowCollapseClickedHandler: (terminal: string) => void;
  onRowHideClickedHandler: (terminal: string) => void;
  onRowSubItemClickedHandler: (terminal: string, tabIndex: number | undefined) => void;
}) {
  const {
    listHeightPx,
    collapsedDatapathUuids,
    hiddenDatapathUuids,
    terminalListItems,
    terminalListSortDirection,
    terminalListSortField,
    terminalMap,
    terminals,
    selectedDatapathUuid,
    selectedTerminalTabIndex,
    hideTtho,
    listDrawerOpen,

    // List Header Click Handlers
    onCollapseGroupClickedHandler,
    onExpandGroupClickedHandler,
    onHideGroupClickedHandler,
    onShowGroupClickedHandler,
    onSortColumnHeaderClickedHandler,
    openListDrawer,

    // List Item Click Handlers
    onRowClickedHandler,
    onRowCollapseClickedHandler,
    onRowHideClickedHandler,
    onRowSubItemClickedHandler,
  } = props;
  // Theme and Style
  const classes = useStyles();

  // Redux Actions
  const dispatch = useDispatch();

  // Redux Selecters
  const isInitialTerminalsRequestFinished = useSelector(selectIsInitialTerminalsRequestFinished, shallowEqual);

  // Ref to Virtual List
  const listRef: RefObject<VariableSizeList | undefined> = useRef<VariableSizeList>();

  // Saves the various groups (key in PlatformListGroup enum) as a GroupListItem array
  const [groupListItems, setGroupListItems] = useState<GroupListItem[]>(
    Object.keys(terminalListItems).map((groupName: string) => ({
      group: groupName as PlatformListGroup,
    })),
  );

  // Combines all the GroupListItems and the respective TerminalListItems into a single array
  const combinedGroupAndTerminalListItems = useMemo(() => {
    return groupListItems.reduce(
      (accumListItems: (TerminalListItem | GroupListItem)[], currentGroupListItem: GroupListItem) =>
        concat(accumListItems, [currentGroupListItem], terminalListItems[currentGroupListItem.group]),
      [],
    );
    // Don't include terminalListItems in dependencies array since it's already a dependency in groupListItems
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupListItems]);

  // Holds the table index of the selected row and only recomputes it when the selected terminal id changes or the
  // terminal data changes
  const memoizedSelectedIndex = useMemo(() => {
    return combinedGroupAndTerminalListItems.findIndex((listItem: GroupListItem | TerminalListItem) => {
      if ('datapathUuidsInGroup' in listItem) {
        // TerminalListItem
        return ((listItem as TerminalListItem).datapathUuidsInGroup || []).find(
          (datapathUuidOnAircraft: string) => datapathUuidOnAircraft === selectedDatapathUuid,
        );
      } else {
        // GroupListItem
        return false;
      }
    });
  }, [selectedDatapathUuid, combinedGroupAndTerminalListItems]);

  // memoizes a callback that only changes when the selected terminal id changes
  const memoizedScrollToItem = useCallback(() => {
    function scrollToItemFunction() {
      if (listRef && listRef.current) {
        if (memoizedSelectedIndex > -1) {
          listRef.current.scrollToItem(memoizedSelectedIndex);
          updateList(listRef);
        }
      }
    }
    scrollToItemFunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDatapathUuid]);

  // When the selected terminal id changes (from clicking a tail tag or clicking a row), scroll to it
  useEffect(() => {
    memoizedScrollToItem();
  }, [memoizedScrollToItem, listDrawerOpen]);

  useEffect(() => {
    updateList(listRef);

    setGroupListItems(
      Object.keys(terminalListItems).map((groupName: string) => ({
        group: groupName as PlatformListGroup,
      })),
    );
  }, [terminalListItems]);

  const terminalListControls = useCallback(
    (groupName?: string) => {
      const group = groupName ? groupName : PlatformListGroup.TERMINAL;
      // Gets an array of datapathUuid strings from the current terminal group
      const datapathUuidsInGroup: string[] = terminals[group].map((terminal: Terminal) => terminal.datapathUuid);
      // Filters out datapathUuids from hiddenDatapathUuids array that don't belong in current terminal group
      const hiddenDatapathUuidsInGroup: string[] = hiddenDatapathUuids.filter((datapathUuid: string) =>
        datapathUuidsInGroup.includes(datapathUuid),
      );
      const sortDirection: string =
        typeof terminalListSortDirection === 'string' ? terminalListSortDirection : terminalListSortDirection[group];

      return (
        <>
          <List data-test="platform-list" dense disablePadding>
            <ListItem disableGutters>
              {terminals[group].every((terminal: Terminal) => !collapsedDatapathUuids[terminal.datapathUuid]) ? (
                <IconButton
                  data-test="toggle-all-collapse-platform-list-button"
                  sx={{
                    minWidth: '34px',
                    maxWidth: '34px',
                  }}
                  onClick={(event: React.MouseEvent) => {
                    updateList(listRef);
                    onCollapseGroupClickedHandler(event, group);
                  }}
                >
                  <KeyboardArrowDown
                    data-test="toggle-all-collapse-platform-list-button--collapsed-icon"
                    className={classes.collapseIcon}
                  />
                </IconButton>
              ) : (
                <IconButton
                  data-test="toggle-all-collapse-platform-list-button"
                  sx={{
                    minWidth: '34px',
                    maxWidth: '34px',
                  }}
                  onClick={(event: React.MouseEvent) => {
                    updateList(listRef);
                    onExpandGroupClickedHandler(event, group);
                  }}
                >
                  <KeyboardArrowRight
                    data-test="toggle-all-collapse-platform-list-button--expanded-icon"
                    className={classes.collapseIcon}
                  />
                </IconButton>
              )}

              {/* Only show Connectivity Status Sort Icon (arrow) if PlatformList is showing TERMINAL group (Fleet view & Terminals In Your Beam Panel) */}
              {group === PlatformListGroup.TERMINAL && (
                <ListItemIcon>
                  <IconButton
                    data-test="platform-list__connectivity-column-header"
                    color="secondary"
                    onClick={(event: React.MouseEvent) => {
                      updateList(listRef);
                      onSortColumnHeaderClickedHandler(
                        'connectivityStatus',
                        sortDirection === SortDirection.ASCENDING
                          ? terminalListSortField !== 'connectivityStatus'
                            ? SortDirection.ASCENDING
                            : SortDirection.DESCENDING
                          : terminalListSortField !== 'connectivityStatus'
                          ? SortDirection.DESCENDING
                          : SortDirection.ASCENDING,
                        group,
                      );
                    }}
                  >
                    {terminalListSortField !== 'connectivityStatus' ? (
                      <TerminalListConnectivityHeaderIcon style={{ fontSize: 18 }} />
                    ) : sortDirection === SortDirection.ASCENDING ? (
                      <ArrowDownwardIcon style={{ fontSize: 18 }} />
                    ) : (
                      <ArrowUpwardIcon style={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </ListItemIcon>
              )}
              <Button
                data-test="platform-list__platform-name-column-header"
                variant="text"
                sx={{
                  color:
                    terminalListSortField === 'terminalDisplayName' && group === PlatformListGroup.TERMINAL // Keep color normal for non-TERMINAL
                      ? appTheme.buttonSelectedColor
                      : appTheme.primaryTextColor,
                  marginLeft: '-12px',
                  letterSpacing: '0.8px',
                }}
                endIcon={
                  terminalListSortField === 'terminalDisplayName' ? (
                    sortDirection === SortDirection.ASCENDING ? (
                      <ArrowDownwardIcon style={{ fontSize: 18 }} />
                    ) : (
                      <ArrowUpwardIcon style={{ fontSize: 18 }} />
                    )
                  ) : null
                }
                onClick={(event: React.MouseEvent) => {
                  updateList(listRef);
                  onSortColumnHeaderClickedHandler(
                    'terminalDisplayName',
                    sortDirection === SortDirection.ASCENDING
                      ? terminalListSortField !== 'terminalDisplayName'
                        ? SortDirection.ASCENDING
                        : SortDirection.DESCENDING
                      : terminalListSortField !== 'terminalDisplayName'
                      ? SortDirection.DESCENDING
                      : SortDirection.ASCENDING,
                    group,
                  );
                }}
              >
                {group}
              </Button>

              <ListItemSecondaryAction className={classes.visibilityToggleContainer}>
                <IconButton
                  data-test="toggle-all-hidden-platform-list-button"
                  onClick={(event: React.MouseEvent) => {
                    if ((hiddenDatapathUuidsInGroup || []).length > 0) {
                      onShowGroupClickedHandler(event, group);
                    } else {
                      onHideGroupClickedHandler(event, group);
                    }
                  }}
                >
                  {(hiddenDatapathUuidsInGroup || []).length ? (
                    <VisibilityIcon
                      data-test="toggle-all-hidden-platform-list-button--show-all-icon"
                      className={classes.visibilityIcon}
                    />
                  ) : (
                    <VisibilityOffIcon
                      data-test="toggle-all-hidden-platform-list-button--hide-all-icon"
                      className={classes.visibilityIcon}
                    />
                  )}
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </>
      );
    },
    [
      terminalListSortDirection,
      terminalListSortField,
      collapsedDatapathUuids,
      hiddenDatapathUuids,
      classes.collapseIcon,
      classes.visibilityIcon,
      classes.visibilityToggleContainer,
      terminals,
      onCollapseGroupClickedHandler,
      onExpandGroupClickedHandler,
      onHideGroupClickedHandler,
      onShowGroupClickedHandler,
      onSortColumnHeaderClickedHandler,
    ],
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={baseTheme}>
        {listDrawerOpen && isInitialTerminalsRequestFinished ? (
          <Box data-test="platform--list" position="absolute" width={KU_PLUS_KA_DRAWER_WIDTH_PX} height="100%">
            {groupListItems.length === 1 && (
              <>
                {terminalListControls(groupListItems[0].group)}
                <Divider />
              </>
            )}
            {/* Subtract length of Group and Terminal list by length of Group list */}
            {combinedGroupAndTerminalListItems.length - groupListItems.length ? (
              <VariableSizeList
                ref={
                  listRef as
                    | string
                    | ((instance: VariableSizeList | null) => void)
                    | RefObject<VariableSizeList>
                    | null
                    | undefined
                }
                itemData={combinedGroupAndTerminalListItems}
                height={listHeightPx}
                width={KU_PLUS_KA_DRAWER_WIDTH_PX}
                itemCount={combinedGroupAndTerminalListItems.length}
                estimatedItemSize={ROW_HEIGHT_BASE_PX}
                // @ts-ignore
                outerElementType={CustomScrollbarsVirtualList}
                itemSize={function itemSize(index) {
                  const listItem: GroupListItem | TerminalListItem = combinedGroupAndTerminalListItems[index];
                  if (!listItem) {
                    // listItem is undefined
                    return 0;
                  } else if ('group' in listItem) {
                    // listItem is GroupListItem
                    if (groupListItems.length === 1) {
                      // Don't display group header if there's only one grouping
                      return 0;
                    } else {
                      return GROUP_HEADER_HEIGHT_PX;
                    }
                  } else if (
                    !!(listItem as TerminalListItem).datapathUuidsInGroup.find(
                      (datapathUuidInGroup: string) => collapsedDatapathUuids[datapathUuidInGroup],
                    )
                  ) {
                    // listItem is TerminalItemList
                    return ROW_HEIGHT_BASE_PX + 1;
                  } else {
                    // listItem is TerminalItemList
                    const spaceNetworkCount = ((listItem as TerminalListItem).spaceNetworks ?? []).length;
                    if ((listItem as TerminalListItem).platformType === TerminalPlatformsEnum.SpaceVehicle) {
                      // Space Vehicle Network list spacing
                      if (spaceNetworkCount > 1) {
                        return MULTI_ROW_HEIGHT_BASE_PX + (spaceNetworkCount - 1) * ROW_HEIGHT_NO_PADDING;
                      } else {
                        return MULTI_ROW_HEIGHT_BASE_PX;
                      }
                    } else if ((listItem as TerminalListItem).datapathUuidsInGroup.length > 1) {
                      return (
                        ROW_HEIGHT_BASE_PX +
                        1 +
                        (listItem as TerminalListItem).datapathUuidsInGroup.length * ROW_HEIGHT_PADDED_PX
                      );
                    } else {
                      return MULTI_ROW_HEIGHT_BASE_PX;
                    }
                  }
                }}
              >
                {(props: any) => {
                  const listItem: GroupListItem | TerminalListItem = props.data[props.index];
                  if (!listItem) {
                    return <div />;
                  } else if ('group' in listItem) {
                    if (groupListItems.length === 1) {
                      // Don't render group header since there's only one grouping and the header is already on the top of the list
                      return null;
                    } else {
                      return (
                        <div style={props.style}>
                          {terminalListControls((listItem as GroupListItem).group)}
                          <Divider />
                        </div>
                      );
                    }
                  }

                  return (
                    <div style={props.style}>
                      <List disablePadding>
                        <PlatformListItemComponent
                          terminalListItem={listItem as TerminalListItem}
                          terminalsInGroup={(listItem as TerminalListItem).datapathUuidsInGroup.map(
                            (datapathUuidInGroup: string) => terminalMap[datapathUuidInGroup],
                          )}
                          key={(listItem as TerminalListItem).datapathUuidsInGroup.join(',')}
                          collapsedDatapathUuids={collapsedDatapathUuids}
                          onRowClickedHandler={onRowClickedHandler}
                          onRowCollapseClickedHandler={onRowCollapseClickedHandler}
                          onRowHideClickedHandler={onRowHideClickedHandler}
                          onRowSubItemClickedHandler={onRowSubItemClickedHandler}
                          listRef={listRef}
                          selectedDatapathUuid={selectedDatapathUuid}
                          selectedTerminalTabIndex={selectedTerminalTabIndex}
                          hideTtho={!!hideTtho}
                        />
                      </List>
                    </div>
                  );
                }}
              </VariableSizeList>
            ) : (
              <Box height="100px" width={KU_PLUS_KA_DRAWER_WIDTH_PX}>
                <Typography data-test="platform-list-no-items-message" variant="subtitle1" align="center">
                  No Items to Display
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box data-test="platform-list--collapsed" maxWidth="32px" position="absolute">
            <Button
              data-test="platform-list--collapsed__expand-button"
              sx={{
                minHeight: `calc(100vh - 108px)`,
                minWidth: '32px',
                maxWidth: '32px',
                alignItems: 'start',
                paddingTop: '22px',
              }}
              onClick={() => {
                dispatch(openListDrawer());
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </Button>
          </Box>
        )}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export const updateList = (listRef: any) => {
  if (listRef && listRef.current) {
    listRef.current.resetAfterIndex(0);
  }
};

const CustomScrollbarsVirtualList = React.forwardRef((props, ref) => (
  <CustomScrollbars {...props} forwardedRef={ref} />
));

const CustomScrollbars = (props: any) => {
  const { onScroll, forwardedRef, style, children } = props;

  const [mouseInBounds, setMouseInBounds] = useState<boolean>(false);
  const refSetter = useCallback(
    scrollbarsRef => {
      if (scrollbarsRef) {
        forwardedRef(scrollbarsRef.view);
      } else {
        forwardedRef(null);
      }
    },
    [forwardedRef],
  );
  return (
    <Scrollbars
      ref={refSetter}
      style={{ ...style, overflow: 'hidden' }}
      onScroll={onScroll}
      autoHeight
      autoHeightMin={style.height}
      onMouseEnter={() => {
        setMouseInBounds(true);
      }}
      onMouseLeave={() => {
        setMouseInBounds(false);
      }}
      renderThumbVertical={({ style, ...props }: any) => {
        return mouseInBounds ? (
          <div
            {...props}
            style={{
              ...style,
              ...SCROLLBARS_THUMB_VERTICAL_STYLE,
              display: SCROLLBARS_THUMB_VERTICAL_STYLE.display,
            }}
          />
        ) : (
          <div />
        );
      }}
    >
      {children}
    </Scrollbars>
  );
};
